import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Gift, Plus, ChevronLeft, ChevronRight, Calendar, Search, Loader2 } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

import { getMonthNames } from "@/utils/dateUtils";

export default function BonusPaymentsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "reports"]);

  const [bonusPayments, setBonusPayments] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    trainer_id: "",
    amount: "",
    reason: "",
    payment_method: "cash",
    payment_date: new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" })
  });

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId, selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch trainers
      const trainersRes = await fetch(`${API}/businesses/${businessId}/trainers`, {
        credentials: "include"
      });
      if (trainersRes.ok) {
        const data = await trainersRes.json();
        setTrainers(data.filter(t => t.status === "active"));
      }

      // Fetch bonus payments
      const paymentsRes = await fetch(
        `${API}/businesses/${businessId}/bonus-payments?month=${selectedMonth}&year=${selectedYear}`,
        { credentials: "include" }
      );
      if (paymentsRes.ok) {
        setBonusPayments(await paymentsRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("common:toast_verilerYuklenirkenHataOlustu"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddBonus = async () => {
    if (!form.trainer_id || !form.amount) {
      toast.error(t("common:trainerAndAmountRequired"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const res = await fetch(`${API}/businesses/${businessId}/bonus-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          trainer_id: form.trainer_id,
          amount: parseTurkishNumber(form.amount),
          reason: form.reason || t("common:extraPayment"),
          payment_method: form.payment_method,
          payment_date: form.payment_date
        })
      });

      if (res.ok) {
        toast.success(t('payments:additionalPaymentSaved'));
        setShowAddModal(false);
        setForm({ trainer_id: "", amount: "", reason: "", payment_method: "cash", payment_date: new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" }) });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      toast.error(t("common:paymentRecordFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const filteredPayments = bonusPayments.filter(p => {
    const trainer = trainers.find(t => t.trainer_id === p.trainer_id);
    const trainerName = trainer ? `${trainer.name} ${trainer.surname}` : "";
    return trainerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (p.reason || "").toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalBonus = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy">{t("common:extraPayments")}</h1>
          <p className="text-grey text-sm">{t('reports:bonusPaymentsDesc')}</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl gap-2">
          <Plus className="w-4 h-4" />
          {t('common:makeExtraPayment')}
        </Button>
      </div>

      {/* Month Navigation & Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-2 bg-white rounded-xl border p-2">
          <Button variant="ghost" size="sm" onClick={handlePreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 min-w-[140px] justify-center">
            <Calendar className="w-4 h-4 text-navy" />
            <span className="font-medium text-navy">
              {getMonthNames()[selectedMonth - 1]} {selectedYear}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-grey" />
          <Input
            placeholder={t('common:searchPlaceholder', 'Ara...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
      </div>

      {/* Summary Card */}
      <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-br from-amber-50 to-orange-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-grey">{t("admin:buAyToplamEkOdeme")}</p>
              <p className="text-3xl font-bold text-amber-700">{formatTurkishCurrency(totalBonus)}</p>
            </div>
            <Gift className="w-12 h-12 text-amber-400" />
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card className="rounded-2xl border-0 shadow-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{t('payments:paymentHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common:date')}</TableHead>
                <TableHead>{t('common:trainer')}</TableHead>
                <TableHead>{t('common:description')}</TableHead>
                <TableHead>{t("common:method")}</TableHead>
                <TableHead className="text-right">{t('common:amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => {
                const trainer = trainers.find(t => t.trainer_id === payment.trainer_id);
                return (
                  <TableRow key={payment.payment_id}>
                    <TableCell className="text-sm">
                      {(payment.payment_date || payment.created_at || "").split("T")[0].split("-").reverse().join(".")}
                    </TableCell>
                    <TableCell className="font-medium">
                      {trainer ? `${trainer.name} ${trainer.surname}` : "-"}
                    </TableCell>
                    <TableCell className="text-grey text-sm">
                      {payment.reason || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {payment.payment_method === "cash" ? t('payments:cash') :
                         payment.payment_method === "bank_transfer" ? "Havale" : payment.payment_method}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold text-red-600">
                      -{formatTurkishCurrency((payment.amount || 0))}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-grey">
                    {t('common:noBonusPaymentRecord')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Bonus Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-600" />
              {t('common:makeExtraPayment')}
            </DialogTitle>
            <DialogDescription>
              {t('reports:bonusPaymentCreateModalDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>{t("common:trainerRequired")}</Label>
              <Select value={form.trainer_id} onValueChange={(v) => setForm({...form, trainer_id: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:selectTrainer")} />
                </SelectTrigger>
                <SelectContent>
                  {trainers.map((trainer) => (
                    <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                      {trainer.name} {trainer.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("common:amountRequired")}</Label>
              <CurrencyInput
                value={form.amount}
                onChange={(val) => setForm({...form, amount: val})}
                placeholder="1000"
                className="rounded-xl"
              />
            </div>

            <div>
              <Label>Ödeme Tarihi *</Label>
              <DatePickerTR
                value={form.payment_date}
                onChange={(v) => setForm({ ...form, payment_date: v })}
                returnDateString
                className="rounded-xl w-full"
                data-testid="bonus-payment-date-picker"
              />
            </div>

            <div>
              <Label>{t('common:description')}</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({...form, reason: e.target.value})}
                placeholder={t("reports:ph_performansBonusuYilbasiPrimiVb")}
                className="rounded-xl"
              />
            </div>

            <div>
              <Label>{t('payments:paymentMethod')}</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowAddModal(false)} disabled={isSaving}>
                {t("common:cancel")}
              </Button>
              <Button className="flex-1 bg-navy hover:bg-navy/90 rounded-xl" onClick={handleAddBonus} disabled={isSaving}>
                {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("common:saving")}</> : t('common:save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
